import { useState } from "react";

type Mode = "login" | "signup";
type Method = "email" | "phone";

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (user: { name: string; avatar: string }) => void;
}

const COUNTRY_CODES = [
  { code: "+86", flag: "🇨🇳", name: "中国" },
  { code: "+1", flag: "🇺🇸", name: "美国" },
  { code: "+44", flag: "🇬🇧", name: "英国" },
  { code: "+81", flag: "🇯🇵", name: "日本" },
  { code: "+82", flag: "🇰🇷", name: "韩国" },
  { code: "+65", flag: "🇸🇬", name: "新加坡" },
  { code: "+60", flag: "🇲🇾", name: "马来西亚" },
  { code: "+61", flag: "🇦🇺", name: "澳大利亚" },
  { code: "+49", flag: "🇩🇪", name: "德国" },
  { code: "+33", flag: "🇫🇷", name: "法国" },
  { code: "+971", flag: "🇦🇪", name: "阿联酋" },
];

export default function AuthModal({ isOpen, onClose, onSuccess }: AuthModalProps) {
  const [mode, setMode] = useState<Mode>("login");
  const [method, setMethod] = useState<Method>("phone");
  const [step, setStep] = useState<"input" | "otp" | "profile">("input");

  // Form fields
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+86");
  const [showCountryPicker, setShowCountryPicker] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [nickname, setNickname] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const selectedCountry = COUNTRY_CODES.find((c) => c.code === countryCode) || COUNTRY_CODES[0];

  const startCountdown = () => {
    setCountdown(60);
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { clearInterval(timer); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const handleSendOtp = () => {
    setError("");
    if (method === "phone" && !phone) { setError("请输入手机号码"); return; }
    if (method === "email" && !email) { setError("请输入邮箱地址"); return; }
    if (method === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("请输入有效的邮箱地址"); return;
    }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      setOtpSent(true);
      setStep("otp");
      startCountdown();
    }, 1200);
  };

  const handleOtpChange = (val: string, idx: number) => {
    if (!/^\d*$/.test(val)) return;
    const next = [...otp];
    next[idx] = val.slice(-1);
    setOtp(next);
    if (val && idx < 5) {
      const nextInput = document.getElementById(`otp-${idx + 1}`);
      nextInput?.focus();
    }
  };

  const handleOtpKeyDown = (e: React.KeyboardEvent, idx: number) => {
    if (e.key === "Backspace" && !otp[idx] && idx > 0) {
      const prevInput = document.getElementById(`otp-${idx - 1}`);
      prevInput?.focus();
    }
  };

  const handleVerifyOtp = () => {
    setError("");
    const code = otp.join("");
    if (code.length < 6) { setError("请输入完整的6位验证码"); return; }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (mode === "signup") {
        setStep("profile");
      } else {
        onSuccess({ name: method === "phone" ? phone : email.split("@")[0], avatar: "U" });
        handleClose();
      }
    }, 1000);
  };

  const handlePasswordLogin = () => {
    setError("");
    if (!email) { setError("请输入邮箱地址"); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setError("请输入有效的邮箱地址"); return; }
    if (!password) { setError("请输入密码"); return; }
    if (mode === "signup" && password !== confirmPassword) { setError("两次密码输入不一致"); return; }
    if (mode === "signup" && password.length < 8) { setError("密码至少需要8位字符"); return; }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (mode === "signup") {
        setStep("profile");
      } else {
        onSuccess({ name: email.split("@")[0], avatar: "U" });
        handleClose();
      }
    }, 1200);
  };

  const handleCompleteProfile = () => {
    setError("");
    if (!nickname.trim()) { setError("请输入您的昵称"); return; }
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      onSuccess({ name: nickname, avatar: nickname[0].toUpperCase() });
      handleClose();
    }, 800);
  };

  const handleClose = () => {
    setStep("input");
    setEmail(""); setPhone(""); setPassword(""); setConfirmPassword("");
    setOtp(["", "", "", "", "", ""]); setNickname("");
    setError(""); setOtpSent(false); setLoading(false);
    onClose();
  };

  const switchMode = (m: Mode) => {
    setMode(m);
    setStep("input");
    setError("");
    setOtp(["", "", "", "", "", ""]);
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={handleClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md mx-4 overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 px-6 pt-8 pb-6">
          <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(ellipse at 30% 50%, #f59e0b 0%, transparent 60%)" }}></div>
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-gray-400 hover:text-white cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl"></i>
          </button>
          <div className="relative flex items-center gap-3 mb-5">
            <img
              src="https://public.readdy.ai/ai/img_res/f704ac6a-a45e-41ab-b34d-a4b4313691a9.png"
              alt="CrownChat"
              className="w-10 h-10 object-contain"
            />
            <div>
              <h2 className="text-white font-bold text-xl" style={{ fontFamily: "'Noto Serif SC', serif" }}>Crowntalk</h2>
              <p className="text-amber-300/70 text-xs">全球社交通讯 · 连接任何人</p>
            </div>
          </div>

          {/* Mode tabs */}
          {step === "input" && (
            <div className="relative flex bg-white/10 rounded-xl p-1">
              {(["login", "signup"] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => switchMode(m)}
                  className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                    mode === m ? "bg-amber-400 text-gray-900" : "text-white/60 hover:text-white"
                  }`}
                >
                  {m === "login" ? "登录" : "注册"}
                </button>
              ))}
            </div>
          )}

          {step === "otp" && (
            <div className="relative">
              <p className="text-amber-300 text-sm font-medium">
                {mode === "login" ? "验证码登录" : "验证码注册"}
              </p>
              <p className="text-white/50 text-xs mt-0.5">
                验证码已发送至 {method === "phone" ? `${countryCode} ${phone}` : email}
              </p>
            </div>
          )}

          {step === "profile" && (
            <div className="relative">
              <p className="text-amber-300 text-sm font-medium">完善个人资料</p>
              <p className="text-white/50 text-xs mt-0.5">最后一步，设置您的 Crowntalk 昵称</p>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {/* Error */}
          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-2">
              <i className="ri-error-warning-line text-red-500 flex-shrink-0"></i>
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* ── STEP: INPUT ── */}
          {step === "input" && (
            <div className="space-y-4">
              {/* Method switcher */}
              <div className="flex bg-gray-100 rounded-xl p-1">
                {(["phone", "email"] as Method[]).map((m) => (
                  <button
                    key={m}
                    onClick={() => { setMethod(m); setError(""); }}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                      method === m ? "bg-white text-gray-900" : "text-gray-400 hover:text-gray-600"
                    }`}
                  >
                    <i className={m === "phone" ? "ri-smartphone-line" : "ri-mail-line"}></i>
                    {m === "phone" ? "手机号" : "邮箱"}
                  </button>
                ))}
              </div>

              {/* Phone input */}
              {method === "phone" && (
                <div>
                  <label className="text-gray-500 text-xs font-medium block mb-1.5">手机号码</label>
                  <div className="flex gap-2">
                    {/* Country code picker */}
                    <div className="relative">
                      <button
                        onClick={() => setShowCountryPicker(!showCountryPicker)}
                        className="flex items-center gap-1.5 border border-gray-200 rounded-xl px-3 py-2.5 text-sm text-gray-700 cursor-pointer hover:border-amber-400 transition-colors whitespace-nowrap bg-white"
                      >
                        <span>{selectedCountry.flag}</span>
                        <span className="font-medium">{countryCode}</span>
                        <i className="ri-arrow-down-s-line text-gray-400 text-xs"></i>
                      </button>
                      {showCountryPicker && (
                        <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 w-48 max-h-52 overflow-y-auto">
                          {COUNTRY_CODES.map((c) => (
                            <button
                              key={c.code}
                              onClick={() => { setCountryCode(c.code); setShowCountryPicker(false); }}
                              className="w-full flex items-center gap-2 px-3 py-2 hover:bg-amber-50 cursor-pointer text-sm text-left transition-colors"
                            >
                              <span>{c.flag}</span>
                              <span className="text-gray-700 flex-1">{c.name}</span>
                              <span className="text-gray-400 text-xs">{c.code}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <input
                      type="tel"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                      placeholder="请输入手机号码"
                      className="flex-1 border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                    />
                  </div>
                  <button
                    onClick={handleSendOtp}
                    disabled={loading || countdown > 0}
                    className={`mt-3 w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                      loading || countdown > 0
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-gray-900"
                    }`}
                  >
                    {loading ? (
                      <><i className="ri-loader-4-line animate-spin mr-1"></i>发送中...</>
                    ) : countdown > 0 ? (
                      `重新发送 (${countdown}s)`
                    ) : (
                      <><i className="ri-message-2-line mr-1"></i>发送验证码</>
                    )}
                  </button>
                </div>
              )}

              {/* Email input */}
              {method === "email" && (
                <div className="space-y-3">
                  <div>
                    <label className="text-gray-500 text-xs font-medium block mb-1.5">邮箱地址</label>
                    <div className="relative">
                      <i className="ri-mail-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="请输入邮箱地址"
                        className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs font-medium block mb-1.5">密码</label>
                    <div className="relative">
                      <i className="ri-lock-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={mode === "signup" ? "至少8位字符" : "请输入密码"}
                        className="w-full border border-gray-200 rounded-xl pl-9 pr-10 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                      />
                      <button
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                      >
                        <i className={showPassword ? "ri-eye-off-line" : "ri-eye-line"}></i>
                      </button>
                    </div>
                  </div>
                  {mode === "signup" && (
                    <div>
                      <label className="text-gray-500 text-xs font-medium block mb-1.5">确认密码</label>
                      <div className="relative">
                        <i className="ri-lock-2-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm"></i>
                        <input
                          type={showPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="再次输入密码"
                          className="w-full border border-gray-200 rounded-xl pl-9 pr-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                        />
                      </div>
                    </div>
                  )}
                  {mode === "login" && (
                    <div className="text-right">
                      <button className="text-amber-600 text-xs hover:underline cursor-pointer">忘记密码？</button>
                    </div>
                  )}
                  <button
                    onClick={handlePasswordLogin}
                    disabled={loading}
                    className={`w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                      loading
                        ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-gray-900"
                    }`}
                  >
                    {loading ? (
                      <><i className="ri-loader-4-line animate-spin mr-1"></i>处理中...</>
                    ) : mode === "login" ? (
                      <><i className="ri-login-box-line mr-1"></i>登录</>
                    ) : (
                      <><i className="ri-user-add-line mr-1"></i>注册账号</>
                    )}
                  </button>
                  {/* Or use OTP */}
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-px bg-gray-100"></div>
                    <span className="text-gray-400 text-xs">或</span>
                    <div className="flex-1 h-px bg-gray-100"></div>
                  </div>
                  <button
                    onClick={handleSendOtp}
                    disabled={loading}
                    className="w-full py-2.5 rounded-xl border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-message-2-line mr-1"></i>使用验证码{mode === "login" ? "登录" : "注册"}
                  </button>
                </div>
              )}

              {/* Social login divider */}
              <div className="flex items-center gap-3 pt-1">
                <div className="flex-1 h-px bg-gray-100"></div>
                <span className="text-gray-400 text-xs">第三方登录</span>
                <div className="flex-1 h-px bg-gray-100"></div>
              </div>
              <div className="flex gap-3">
                {[
                  { icon: "ri-wechat-line", label: "微信", color: "text-green-500 border-green-200 hover:bg-green-50" },
                  { icon: "ri-weibo-line", label: "微博", color: "text-red-500 border-red-200 hover:bg-red-50" },
                  { icon: "ri-google-line", label: "Google", color: "text-orange-500 border-orange-200 hover:bg-orange-50" },
                  { icon: "ri-apple-line", label: "Apple", color: "text-gray-800 border-gray-200 hover:bg-gray-50" },
                ].map((s) => (
                  <button
                    key={s.label}
                    title={`使用${s.label}登录`}
                    className={`flex-1 flex items-center justify-center py-2.5 border rounded-xl cursor-pointer transition-colors ${s.color}`}
                  >
                    <i className={`${s.icon} text-lg`}></i>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* ── STEP: OTP ── */}
          {step === "otp" && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="w-14 h-14 flex items-center justify-center bg-amber-50 rounded-full mx-auto mb-3">
                  <i className="ri-message-2-line text-amber-500 text-2xl"></i>
                </div>
                <p className="text-gray-700 font-semibold text-sm">
                  验证码已发送至
                </p>
                <p className="text-amber-600 font-bold text-base mt-0.5">
                  {method === "phone" ? `${countryCode} ${phone}` : email}
                </p>
                <p className="text-gray-400 text-xs mt-1">请在5分钟内输入6位验证码</p>
              </div>

              {/* OTP boxes */}
              <div className="flex gap-2 justify-center">
                {otp.map((digit, idx) => (
                  <input
                    key={idx}
                    id={`otp-${idx}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(e.target.value, idx)}
                    onKeyDown={(e) => handleOtpKeyDown(e, idx)}
                    className={`w-11 h-12 text-center text-xl font-bold border-2 rounded-xl outline-none transition-all ${
                      digit ? "border-amber-400 bg-amber-50 text-gray-900" : "border-gray-200 text-gray-400"
                    } focus:border-amber-400`}
                  />
                ))}
              </div>

              <button
                onClick={handleVerifyOtp}
                disabled={loading || otp.join("").length < 6}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  loading || otp.join("").length < 6
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-gray-900"
                }`}
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i>验证中...</>
                ) : (
                  <><i className="ri-shield-check-line mr-1"></i>验证并{mode === "login" ? "登录" : "注册"}</>
                )}
              </button>

              <div className="flex items-center justify-between text-sm">
                <button
                  onClick={() => setStep("input")}
                  className="text-gray-400 hover:text-gray-600 cursor-pointer text-xs"
                >
                  <i className="ri-arrow-left-line mr-1"></i>修改{method === "phone" ? "手机号" : "邮箱"}
                </button>
                <button
                  onClick={() => { if (countdown === 0) { startCountdown(); } }}
                  disabled={countdown > 0}
                  className={`text-xs cursor-pointer ${countdown > 0 ? "text-gray-300 cursor-not-allowed" : "text-amber-600 hover:underline"}`}
                >
                  {countdown > 0 ? `重新发送 (${countdown}s)` : "重新发送验证码"}
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: PROFILE ── */}
          {step === "profile" && (
            <div className="space-y-5">
              <div className="text-center">
                <div className="w-20 h-20 flex items-center justify-center bg-gradient-to-br from-amber-400 to-yellow-500 rounded-full mx-auto mb-3 text-gray-900 text-3xl font-bold">
                  {nickname ? nickname[0].toUpperCase() : <i className="ri-user-line"></i>}
                </div>
                <p className="text-gray-500 text-xs">点击可更换头像</p>
              </div>

              <div>
                <label className="text-gray-500 text-xs font-medium block mb-1.5">
                  昵称 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  placeholder="给自己起个好听的名字"
                  maxLength={20}
                  className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-amber-400 transition-colors"
                />
                <p className="text-gray-300 text-xs mt-1 text-right">{nickname.length}/20</p>
              </div>

              <div className="bg-amber-50 border border-amber-100 rounded-xl p-4">
                <p className="text-amber-700 text-xs font-medium mb-2">
                  <i className="ri-gift-line mr-1"></i>注册成功即可获得：
                </p>
                <div className="space-y-1.5">
                  {[
                    "免费全球视频通话 30 分钟体验",
                    "皇冠鸿运商城新人优惠券 ¥50",
                    "Crowntalk 专属会员徽章",
                  ].map((item) => (
                    <div key={item} className="flex items-center gap-2 text-amber-600 text-xs">
                      <i className="ri-check-line flex-shrink-0"></i>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <button
                onClick={handleCompleteProfile}
                disabled={loading || !nickname.trim()}
                className={`w-full py-3 rounded-xl font-bold text-sm transition-all cursor-pointer whitespace-nowrap ${
                  loading || !nickname.trim()
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-gradient-to-r from-amber-400 to-yellow-500 hover:from-amber-500 hover:to-yellow-600 text-gray-900"
                }`}
              >
                {loading ? (
                  <><i className="ri-loader-4-line animate-spin mr-1"></i>创建账号中...</>
                ) : (
                  <><i className="ri-rocket-line mr-1"></i>完成注册，开始使用！</>
                )}
              </button>
            </div>
          )}
        </div>

        {/* Footer note */}
        <div className="px-6 pb-5 text-center">
          <p className="text-gray-400 text-xs">
            {mode === "login" ? "还没有账号？" : "已有账号？"}
            <button
              onClick={() => switchMode(mode === "login" ? "signup" : "login")}
              className="text-amber-600 font-medium hover:underline cursor-pointer ml-1"
            >
              {mode === "login" ? "立即注册" : "直接登录"}
            </button>
          </p>
          <p className="text-gray-300 text-xs mt-1.5">
            注册即表示同意
            <span className="text-gray-400 cursor-pointer hover:underline mx-1">《用户协议》</span>
            和
            <span className="text-gray-400 cursor-pointer hover:underline mx-1">《隐私政策》</span>
          </p>
        </div>
      </div>
    </div>
  );
}
